import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { clearAuthenticatedClientState } from '@/lib/authSession';

export const IDLE_SIGN_OUT_MS = 30 * 60 * 1000;
export const IDLE_WARNING_MS = 5 * 60 * 1000;

const ACTIVITY_BROADCAST_THROTTLE_MS = 15 * 1000;
const ACTIVITY_CHANNEL_NAME = 'worklynx-session-activity';
const ACTIVITY_STORAGE_KEY = 'worklynx:session:last-activity';
const SIGN_OUT_STORAGE_KEY = 'worklynx:session:sign-out';
const ACTIVITY_EVENTS = ['keydown', 'pointerdown', 'scroll', 'touchstart'] as const;

type SessionActivityMessage =
  | { type: 'activity'; at: number }
  | { type: 'sign-out'; at: number };

interface UseIdleSignOutOptions {
  enabled: boolean;
  timeoutMs?: number;
  warningMs?: number;
}

export interface IdleSessionState {
  phase: 'active' | 'warning' | 'expired';
  remainingMs: number;
}

export function getIdleSessionState(
  lastActivityAt: number,
  now: number,
  timeoutMs: number,
  warningMs: number,
): IdleSessionState {
  const remainingMs = Math.max(0, timeoutMs - Math.max(0, now - lastActivityAt));

  if (remainingMs === 0) return { phase: 'expired', remainingMs };
  if (remainingMs <= warningMs) return { phase: 'warning', remainingMs };
  return { phase: 'active', remainingMs };
}

export function useIdleSignOut({
  enabled,
  timeoutMs = IDLE_SIGN_OUT_MS,
  warningMs = IDLE_WARNING_MS,
}: UseIdleSignOutOptions) {
  const queryClient = useQueryClient();
  const lastActivityRef = useRef(Date.now());
  const lastBroadcastRef = useRef(0);
  const signingOutRef = useRef(false);
  const channelRef = useRef<BroadcastChannel | null>(null);
  const [warningOpen, setWarningOpen] = useState(false);
  const [remainingMs, setRemainingMs] = useState(timeoutMs);

  const publishMessage = useCallback((message: SessionActivityMessage) => {
    channelRef.current?.postMessage(message);

    const key = message.type === 'activity' ? ACTIVITY_STORAGE_KEY : SIGN_OUT_STORAGE_KEY;
    window.localStorage.setItem(key, String(message.at));
  }, []);

  const signOutForIdle = useCallback(
    async (publish = true) => {
      if (signingOutRef.current) return;

      signingOutRef.current = true;
      setWarningOpen(false);
      if (publish) publishMessage({ type: 'sign-out', at: Date.now() });

      try {
        await supabase.auth.signOut({ scope: 'local' });
      } finally {
        window.localStorage.removeItem(ACTIVITY_STORAGE_KEY);
        clearAuthenticatedClientState(queryClient);
      }
    },
    [publishMessage, queryClient],
  );

  const recordActivity = useCallback(
    (forceBroadcast = false) => {
      const now = Date.now();
      lastActivityRef.current = now;
      setWarningOpen(false);
      setRemainingMs(timeoutMs);

      if (forceBroadcast || now - lastBroadcastRef.current >= ACTIVITY_BROADCAST_THROTTLE_MS) {
        lastBroadcastRef.current = now;
        publishMessage({ type: 'activity', at: now });
      }
    },
    [publishMessage, timeoutMs],
  );

  useEffect(() => {
    if (!enabled) {
      signingOutRef.current = false;
      setWarningOpen(false);
      setRemainingMs(timeoutMs);
      return undefined;
    }

    signingOutRef.current = false;
    if ('BroadcastChannel' in window) {
      channelRef.current = new BroadcastChannel(ACTIVITY_CHANNEL_NAME);
    }

    const acceptActivity = (at: number) => {
      if (!Number.isFinite(at) || at <= lastActivityRef.current || at > Date.now() + 5000) return;
      lastActivityRef.current = at;
      setWarningOpen(false);
      setRemainingMs(timeoutMs);
    };

    const handleMessage = (message: SessionActivityMessage) => {
      if (message.type === 'activity') {
        acceptActivity(message.at);
      } else if (message.type === 'sign-out') {
        void signOutForIdle(false);
      }
    };

    const handleBroadcast = (event: MessageEvent<SessionActivityMessage>) => {
      handleMessage(event.data);
    };

    const handleStorage = (event: StorageEvent) => {
      if (!event.newValue) return;
      const at = Number(event.newValue);
      if (event.key === ACTIVITY_STORAGE_KEY) acceptActivity(at);
      if (event.key === SIGN_OUT_STORAGE_KEY) void signOutForIdle(false);
    };

    const handleLocalActivity = () => {
      if (document.visibilityState === 'hidden') return;
      recordActivity();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') recordActivity(true);
    };

    const evaluateIdleState = () => {
      const state = getIdleSessionState(lastActivityRef.current, Date.now(), timeoutMs, warningMs);
      setRemainingMs(state.remainingMs);
      setWarningOpen(state.phase === 'warning');
      if (state.phase === 'expired') void signOutForIdle();
    };

    channelRef.current?.addEventListener('message', handleBroadcast);
    window.addEventListener('storage', handleStorage);
    window.addEventListener('focus', handleLocalActivity);
    ACTIVITY_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, handleLocalActivity, { passive: true });
    });
    document.addEventListener('visibilitychange', handleVisibilityChange);

    recordActivity(true);
    const interval = window.setInterval(evaluateIdleState, 1000);

    return () => {
      window.clearInterval(interval);
      channelRef.current?.removeEventListener('message', handleBroadcast);
      channelRef.current?.close();
      channelRef.current = null;
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('focus', handleLocalActivity);
      ACTIVITY_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, handleLocalActivity);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enabled, recordActivity, signOutForIdle, timeoutMs, warningMs]);

  return {
    warningOpen,
    remainingMs,
    staySignedIn: () => recordActivity(true),
    signOutNow: () => signOutForIdle(),
  };
}
